import type { Node, Edge, useReactFlow } from "reactflow";
import { toast } from "@/components/ui/notify";
import { v4 as uuid } from "uuid";
import { cancelWithConfirm } from "./useCanvasCancelHandler";
import type { CanvasNode, CanvasEdge, LayoutNodeType } from "@opus-dt/shared-types";
import type { CanvasNodeData } from "@/types/canvas.types.js";
import type { MachineDetail } from "@/features/lines/hooks";
import type { useUpdateCanvas } from "../../hooks";
import type { ValidationResult } from "../../canvas-validator";
import { defaultsForType } from "../helpers";
import { createCanvasSaveHandler } from "./useCanvasSaveHandler";

export interface UseCanvasMutationsParams {
  isEdit: boolean;
  isRunning: boolean;
  isDirty: boolean;
  draftNodes: Node<CanvasNodeData>[];
  draftEdges: Edge[];
  selectedNodeId: string | null;
  draftCanvasNodes: CanvasNode[];
  draftCanvasEdges: CanvasEdge[];
  validation: ValidationResult;
  setDraftNodes: React.Dispatch<React.SetStateAction<Node<CanvasNodeData>[]>>;
  setDraftEdges: React.Dispatch<React.SetStateAction<Edge[]>>;
  setIsDirty: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedNodeId: React.Dispatch<React.SetStateAction<string | null>>;
  announce: (msg: string) => void;
  flow: ReturnType<typeof useReactFlow>;
  update: ReturnType<typeof useUpdateCanvas>;
  onMachineClick?: (m: MachineDetail) => void;
  onSaved?: () => void;
  onCancel?: () => void;
  machineById: Map<string, MachineDetail>;
  onSaveOverride?: () => Promise<void>;
  onBeforeSave?: () => Promise<void>;
}

export interface CanvasMutations {
  handleAddNode: (type: LayoutNodeType) => void;
  handleAddMachineFromPicker: (machine: { id: string; name: string }) => void;
  handleNodeClick: (e: unknown, node: Node<CanvasNodeData>) => void;
  handleNodeChange: (next: Partial<CanvasNode>) => void;
  handleNodeDelete: () => void;
  handleSave: () => Promise<void>;
  handleCancel: () => Promise<void>;
}

export function useCanvasMutations(params: UseCanvasMutationsParams): CanvasMutations {
  const {
    isEdit,
    isRunning,
    isDirty,
    draftNodes,
    draftEdges,
    selectedNodeId,
    draftCanvasNodes,
    draftCanvasEdges,
    validation,
    setDraftNodes,
    setDraftEdges,
    setIsDirty,
    setSelectedNodeId,
    announce,
    flow,
    update,
    onMachineClick,
    onSaved,
    onCancel,
    machineById,
    onSaveOverride,
    onBeforeSave,
  } = params;

  function viewportCenter() {
    const { x, y, zoom } = flow.getViewport();
    return {
      x: -x / (zoom || 1) + 300,
      y: -y / (zoom || 1) + 200,
    };
  }

  function handleAddNode(type: LayoutNodeType) {
    if (!isEdit) return;
    if (type === "MACHINE") return;
    const defaults = defaultsForType(type);
    const newNode: Node<CanvasNodeData> = {
      id: uuid(),
      type,
      position: viewportCenter(),
      data: { ...defaults.data, label: defaults.label },
      draggable: true,
      selectable: true,
      connectable: true,
    };
    setDraftNodes((prev) => [...prev, newNode]);
    setSelectedNodeId(newNode.id);
    setIsDirty(true);
    announce(`${defaults.label} adicionado ao canvas.`);
  }

  function handleAddMachineFromPicker(machine: { id: string; name: string }) {
    if (!isEdit) return;
    const newNode: Node<CanvasNodeData> = {
      id: uuid(),
      type: "MACHINE",
      position: viewportCenter(),
      data: { machineId: machine.id, label: machine.name },
      draggable: true,
      selectable: true,
      connectable: true,
    };
    setDraftNodes((prev) => [...prev, newNode]);
    setSelectedNodeId(newNode.id);
    setIsDirty(true);
    announce(`Máquina ${machine.name} adicionada ao canvas.`);
  }

  function handleNodeClick(_: unknown, node: Node<CanvasNodeData>) {
    if (isEdit) {
      setSelectedNodeId(node.id);
      announce(`Nó ${node.data.label ?? node.id} selecionado.`);
      return;
    }
    if (!onMachineClick) return;
    if (node.type !== "MACHINE") return;
    const mid = node.data.machineId;
    if (!mid) {
      toast.info("Nó não vinculado a uma máquina.");
      return;
    }
    const machine = machineById.get(mid);
    if (!machine) {
      toast.info("Máquina removida do cadastro. Edite o canvas para desvincular o nó.");
      return;
    }
    onMachineClick(machine);
  }

  function handleNodeChange(next: Partial<CanvasNode>) {
    if (!selectedNodeId) return;
    setDraftNodes((prev) =>
      prev.map((n) =>
        n.id === selectedNodeId
          ? {
              ...n,
              data: {
                ...n.data,
                ...(next.label !== undefined ? { label: next.label } : {}),
                ...(next.data ?? {}),
              },
            }
          : n
      )
    );
    setIsDirty(true);
  }

  function handleNodeDelete() {
    if (!selectedNodeId) return;
    const target = draftNodes.find((n) => n.id === selectedNodeId);
    if (target?.type === "START" || target?.type === "END") {
      toast.info("INÍCIO e FIM não podem ser removidos.");
      return;
    }
    const removedEdges = draftEdges.filter(
      (e) => e.source === selectedNodeId || e.target === selectedNodeId
    ).length;
    setDraftNodes((prev) => prev.filter((n) => n.id !== selectedNodeId));
    setDraftEdges((prev) =>
      prev.filter((e) => e.source !== selectedNodeId && e.target !== selectedNodeId)
    );
    setSelectedNodeId(null);
    setIsDirty(true);
    announce(
      `Nó ${target?.data.label ?? selectedNodeId} removido${
        removedEdges > 0 ? ` junto com ${removedEdges} conexão(ões)` : ""
      }.`
    );
  }

  const handleSave = createCanvasSaveHandler({
    isRunning,
    validation,
    draftCanvasNodes,
    draftCanvasEdges,
    flow,
    update,
    setIsDirty,
    onSaved,
    onSaveOverride,
    onBeforeSave,
  });

  const handleCancel = () => cancelWithConfirm(isDirty, setIsDirty, onCancel);

  return {
    handleAddNode,
    handleAddMachineFromPicker,
    handleNodeClick,
    handleNodeChange,
    handleNodeDelete,
    handleSave,
    handleCancel,
  };
}
